function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { width, space, themeGet } from 'styled-system';
import { deprecatedPropType, applySizes, applyVariations, getPaletteColor, getTextColorOn, deprecatedColorValue } from '../utils';
var sizes = {
  small: css(["border-radius:", ";font-size:", "px;padding:7px 12px;"], themeGet('borderRadii.action-sm'), themeGet('fontSizes.0')),
  medium: css(["border-radius:", ";font-size:", "px;padding:9.5px 18px;"], themeGet('borderRadii.action-md'), themeGet('fontSizes.1')),
  large: css(["border-radius:", ";font-size:", "px;padding:12px 22px;"], themeGet('borderRadii.action-lg'), themeGet('fontSizes.2'))
};
var variations = {
  fill: css(["background-color:", ";color:", ";&:hover{background-color:", ";", "}&:focus{outline:", ";box-shadow:", ";}"], function (props) {
    return getPaletteColor(props.disabled ? 'light' : 'base')(props);
  }, function (props) {
    return getTextColorOn(props.disabled ? 'light' : 'base')(props);
  }, function (props) {
    return getPaletteColor(props.disabled ? 'light' : 'dark')(props);
  }, function (props) {
    return props.disabled ? '' : "color: ".concat(getTextColorOn('dark')(props), ";");
  }, function (props) {
    return "0px solid ".concat(getPaletteColor(props.disabled ? '' : 'dark')(props));
  }, function (props) {
    return " 0 0 0 2px  ".concat(getPaletteColor(props.disabled ? '' : 'dark')(props));
  }),
  link: css(["-webkit-font-smoothing:inherit;font-weight:", ";color:", ";line-height:", ";vertical-align:inherit;padding:0;background-color:transparent;&:hover{color:", ";text-decoration:underline;}"], function (props) {
    return props.theme.fontWeights.medium;
  }, getPaletteColor('base'), function (props) {
    return props.theme.lineHeights.standard;
  }, getPaletteColor('dark')),
  outline: css(["color:", ";box-shadow:inset 0 0 0 2px ", ";background-color:transparent;&:hover{background-color:transparent;", "}"], function (props) {
    return getPaletteColor(props.disabled ? 'light' : 'base')(props);
  }, function (props) {
    return getPaletteColor(props.disabled ? 'light' : 'base')(props);
  }, function (props) {
    return props.disabled ? '' : "\n      color: ".concat(getPaletteColor('dark')(props), ";\n      box-shadow: inset 0 0 0 2px ").concat(getPaletteColor('dark')(props), ";\n    ");
  }),
  plain: css(["background-color:transparent;color:", ";&:hover{background-color:", ";color:", ";}&:focus{outline:0px solid ", ";box-shadow:0 0 0 2px ", ";}"], getPaletteColor('base'), getPaletteColor('background.light'), getPaletteColor('dark'), getPaletteColor('dark'), getPaletteColor('dark')),
  subtle: css(["background-color:", ";color:", ";&:hover{background-color:", ";color:", ";}&:focus{outline:0px solid ", ";box-shadow:0 0 0 2px ", ";}"], getPaletteColor('background.base'), getPaletteColor('base'), getPaletteColor('background.light'), getPaletteColor('dark'), getPaletteColor('dark'), getPaletteColor('dark')),
  lightFill: css(["background-color:", ";color:", ";&:hover{background-color:", ";color:", ";}&:focus{outline:", ";box-shadow:", ";}"], getPaletteColor('light'), getPaletteColor('base'), getPaletteColor('light'), getPaletteColor('dark'), function (props) {
    return "0px solid ".concat(getPaletteColor(props.disabled ? '' : 'dark')(props));
  }, function (props) {
    return " 0 0 0 2px  ".concat(getPaletteColor(props.disabled ? '' : 'dark')(props));
  })
};
export var buttonStyles = css(["-webkit-font-smoothing:antialiased;display:inline-block;vertical-align:middle;text-align:center;text-decoration:none;font-family:inherit;font-weight:", ";line-height:1.5;cursor:", ";border-radius:", ";border-width:0;border-style:solid;", ";", ";", ";", ";&:disabled{cursor:not-allowed;color:", ";background-color:", ";}"], function (props) {
  return props.theme.fontWeights.bold;
}, function (props) {
  return props.disabled ? 'default' : 'pointer';
}, function (props) {
  return props.theme.radius;
}, function (_ref) {
  var theme = _ref.theme;
  return applySizes(sizes, 'medium', theme.mediaQueries);
}, applyVariations('Button', variations), width, space, getPaletteColor('text.light'), getPaletteColor('background.base'));
/**
 * Use the <Button /> component to render a primitive button. Use the `variation` prop to change the look of the button.
 */

var Button = styled.button.attrs(function (_ref2) {
  var width = _ref2.width,
      fullWidth = _ref2.fullWidth,
      title = _ref2.title,
      ariaLabel = _ref2['aria-label'];
  return {
    width: fullWidth ? 1 : width,
    'aria-label': ariaLabel || title
  };
}).withConfig({
  displayName: "Button",
  componentId: "sc-27clrj-0"
})(["", ""], buttonStyles);
Button.propTypes = _objectSpread(_objectSpread(_objectSpread({
  size: PropTypes.oneOf(['small', 'medium', 'large'])
}, width.propTypes), space.propTypes), {}, {
  fullWidth: deprecatedPropType('width'),
  variation: PropTypes.oneOf(Object.keys(variations)),
  color: deprecatedColorValue(),
  disabled: PropTypes.bool
});
Button.defaultProps = {
  color: 'primary',
  size: 'medium',
  variation: 'fill'
};
Button.displayName = 'Button';
export default Button;